import { useState } from 'react'
import { styled } from 'styled-components'
import createClass, { Class, deleteClass } from '../../../libs/class-service'
import { CiSettings } from 'react-icons/ci'
import { MdDelete } from 'react-icons/md'
import AddClassModal from './AddClassModal'
import ConfirmDeleteModal from './ConfirmDeleteModal'

interface Props {
    classes: Class[]
    onManageClass?: (classItem: Class) => void
    onClassCreated?: () => void
}

const Container = styled.div`
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: 30px;
`

const ClassesSection = styled.div`
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: 20px;
`

const ClassesHeading = styled.h2`
    font-size: 24px;
    font-weight: 600;
    color: #000000;
    margin: 0;
    padding-bottom: 10px;
    border-bottom: 2px solid #e5e6eb;
`

const ClassItem = styled.div`
    display: grid;
    grid-template-columns: 1fr auto auto;
    gap: 20px;
    align-items: center;
    padding: 16px 20px;
    border-bottom: 1px solid #e5e6eb;

    &:last-child {
        border-bottom: none;
    }
`

const ClassName = styled.div`
    font-size: 18px;
    font-weight: 500;
    color: #333333;
`

const ButtonGroup = styled.div`
    display: flex;
    gap: 12px;
    align-items: center;
`

const ActionButton = styled.button`
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px 16px;
    border: 1px solid #e5e6eb;
    border-radius: 6px;
    background-color: #ffffff;
    color: #1fa474;
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s;

    &:hover {
        background-color: #f2f3f5;
        border-color: #1fa474;
    }

    svg {
        width: 16px;
        height: 16px;
    }
`

const DeleteButton = styled.button`
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px 16px;
    border: 1px solid #e5e6eb;
    border-radius: 6px;
    background-color: #ffffff;
    color: #d32f2f;
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s;

    &:hover {
        background-color: #ffebee;
        border-color: #d32f2f;
    }

    svg {
        width: 16px;
        height: 16px;
    }
`

const UploadMoreButton = styled.button`
    width: fit-content;
    padding: 12px 32px;
    background-color: #1fa474;
    color: #ffffff;
    border: none;
    border-radius: 50px;
    font-size: 16px;
    font-weight: 600;
    cursor: pointer;
    transition: background-color 0.2s;
    margin-top: 20px;
    align-self: center;

    &:hover {
        background-color: #188c5f;
    }
`

export default function ClassesList({
    classes,
    onManageClass,
    onClassCreated,
}: Props) {
    const [showModal, setShowModal] = useState(false)
    const [newClassName, setNewClassName] = useState('')
    const [isLoading, setIsLoading] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false)
    const [classToDelete, setClassToDelete] = useState<Class | null>(null)
    const [isDeleting, setIsDeleting] = useState(false)

    console.log('ClassesList - classes data:', classes)
    console.log('ClassesList - classes length:', classes?.length || 0)
    if (classes && classes.length > 0) {
        console.log('First class item:', classes[0])
        console.log('First class keys:', Object.keys(classes[0]))
    }

    const handleManageClass = (classItem: Class) => {
        if (onManageClass) {
            onManageClass(classItem)
        }
    }

    const handleAddClassClick = () => {
        setShowModal(true)
    }

    const handleCloseModal = () => {
        setShowModal(false)
        setNewClassName('')
        setError(null)
    }

    const handleSubmitClass = async () => {
        if (!newClassName.trim()) return

        setIsLoading(true)
        setError(null)
        try {
            const userId = localStorage.getItem('userId')
            if (!userId) {
                throw new Error('User not authenticated')
            }

            const response = await createClass({
                classname: newClassName.trim(),
                teacher_id: parseInt(userId, 10),
            })

            console.log('New class created:', response)
            handleCloseModal()
            // Trigger refresh of classes list in parent component
            if (onClassCreated) {
                onClassCreated()
            }
        } catch (err) {
            console.error('Error creating class:', err)
            setError(
                err instanceof Error ? err.message : 'Failed to create class'
            )
        } finally {
            setIsLoading(false)
        }
    }

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setNewClassName(e.target.value)
    }

    const handleDeleteClick = (classItem: Class) => {
        setClassToDelete(classItem)
        setDeleteConfirmOpen(true)
    }

    const handleCloseDeleteModal = () => {
        setDeleteConfirmOpen(false)
        setClassToDelete(null)
    }

    const handleConfirmDelete = async () => {
        if (!classToDelete) return

        setIsDeleting(true)
        try {
            await deleteClass(classToDelete.id)
            console.log('Class deleted:', classToDelete.class_name)
            handleCloseDeleteModal()
            // Trigger refresh of classes list in parent component
            if (onClassCreated) {
                onClassCreated()
            }
        } catch (err) {
            console.error('Error deleting class:', err)
            // You might want to show an error message here
        } finally {
            setIsDeleting(false)
        }
    }

    return (
        <Container>
            <ClassesSection>
                <ClassesHeading>My Classes</ClassesHeading>
                {classes.map((classItem) => (
                    <ClassItem key={classItem.id}>
                        <ClassName>{classItem.class_name}</ClassName>
                        <ButtonGroup>
                            <ActionButton
                                onClick={() => handleManageClass(classItem)}
                            >
                                <CiSettings />
                                Manage Class
                            </ActionButton>
                            <DeleteButton
                                onClick={() => handleDeleteClick(classItem)}
                            >
                                <MdDelete />
                                Remove
                            </DeleteButton>
                        </ButtonGroup>
                    </ClassItem>
                ))}
            </ClassesSection>
            <UploadMoreButton onClick={handleAddClassClick}>
                Add Class
            </UploadMoreButton>

            <AddClassModal
                isOpen={showModal}
                onClose={handleCloseModal}
                onSubmit={handleSubmitClass}
                className={newClassName}
                onClassNameChange={handleInputChange}
                isLoading={isLoading}
                error={error}
            />

            <ConfirmDeleteModal
                isOpen={deleteConfirmOpen}
                onClose={handleCloseDeleteModal}
                onConfirm={handleConfirmDelete}
                title="Delete Class"
                message={`Are you sure you want to delete "${classToDelete?.class_name}"? This action cannot be undone.`}
                isLoading={isDeleting}
            />
        </Container>
    )
}
